import apiClient from './api';
import { Preset, PresetCreateRequest, PresetListResponse, PresetUpdateRequest, TradeSet, TradeSetCreateRequest } from '../types/preset';

const PRESET_BASE_URL = '/preset';
const TRADESET_BASE_URL = '/tradeset'; // Assume a base URL for tradesets

export const presetService = {
  // Get all presets with pagination and filtering
  getPresets: async (page = 1, size = 10, search?: string, symbol?: string): Promise<PresetListResponse> => {
    let url = `${PRESET_BASE_URL}/?page=${page}&size=${size}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (symbol) {
      url += `&symbol=${encodeURIComponent(symbol)}`;
    }
    const response = await apiClient.get(url);
    return response.data;
  },

  // Get preset by ID
  getPresetById: async (id: string): Promise<Preset> => {
    const response = await apiClient.get(`${PRESET_BASE_URL}/${id}`);
    return response.data;
  },

  // Get preset by name
  getPresetByName: async (name: string): Promise<Preset> => {
    const response = await apiClient.get(`${PRESET_BASE_URL}/name/${encodeURIComponent(name)}`);
    return response.data;
  },

  // Check if preset name exists (for validation)
  checkPresetNameExists: async (name: string, excludeId?: string): Promise<boolean> => {
    try {
      const preset = await presetService.getPresetByName(name);
      // If we're editing and found the same preset ID, it's not a duplicate
      if (excludeId && preset.id === excludeId) {
        return false; // Same preset, not a duplicate
      }
      return true; // Different preset with same name exists
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false; // 404 means preset doesn't exist
      }
      // For other errors (500, network, etc.), we'll assume it might exist to be safe
      throw error;
    }
  },

  // Search presets
  searchPresets: async (query: string): Promise<Preset[]> => {
    const response = await apiClient.get(`${PRESET_BASE_URL}/search/?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  // Create new preset
  createPreset: async (preset: PresetCreateRequest): Promise<Preset> => {
    const response = await apiClient.post(PRESET_BASE_URL, preset);
    return response.data;
  },

  // Update preset
  updatePreset: async (id: string, preset: PresetUpdateRequest): Promise<Preset> => {
    const response = await apiClient.put(`${PRESET_BASE_URL}/${id}`, preset);
    return response.data;
  },

  // Delete preset
  deletePreset: async (id: string): Promise<void> => {
    await apiClient.delete(`${PRESET_BASE_URL}/${id}`);
  },

  // Execute preset
  executePreset: async (id: string): Promise<{ success: boolean; message: string; orderId?: string }> => {
    const response = await apiClient.post(`${PRESET_BASE_URL}/${id}/execute`);
    return response.data;
  },

  // === TradeSet Endpoints ===
  // TODO: Add getTradeSets, getTradeSetById, updateTradeSet, deleteTradeSet etc.
  
  // Create new tradeSet
  createTradeSet: async (tradeSet: TradeSetCreateRequest): Promise<TradeSet> => {
    const response = await apiClient.post(TRADESET_BASE_URL, tradeSet);
    return response.data;
  },
}; 